import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { useDialog } from '../components/ConfirmProvider';
import { formatDate } from '../utils/dateFormat';
import {
  FaUser, FaBuilding, FaSearch, FaTimes, FaInfoCircle, FaShieldAlt, FaFolderOpen,
  FaPlus, FaUpload
} from 'react-icons/fa';
import { exportBrandedPdf, PDF_COLOURS } from '../utils/brandedPdf';
import CustomerForm from './CustomerForm';
import BulkCustomerUpload from './BulkCustomerUpload';
import RiskAssessmentView from './RiskAssessmentView';
import CustomerAuditTrail from './CustomerAuditTrail';
import {
  PRODUCT_TYPES, DELIVERY_CHANNELS, NATURE_OF_BUSINESS, NATIONALITY_CATEGORIES, PIP_ASSOCIATE_OPTIONS,
  OCCUPATIONS, SOURCES_OF_INCOME, TRANSACTION_SOURCES_OF_FUNDS, INSTITUTION_TYPES, BO_PIP_OPTIONS, optionLabel
} from './riskOptions';
import '../Pages.css';
import '../DataCapturer/StepperForm.css';
import './Customers.css';

const formatLastUpdated = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
};

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

// "Privileged" customer manager — system admin OR has any customer-management perm.
// Drives whether the user sees the admin-style table search vs the search-hero card.
const isCustomerManager = (user) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && (
      user.permissions.includes('customers.create') ||
      user.permissions.includes('customers.update') ||
      user.permissions.includes('customers.toggle')
    ))
  );

const CUSTOMER_TYPES = ['individual','sme','agent','corporate','internal_wallet','partner','vas_provider'];
const TYPE_LABELS = {
  individual:      'Individual',
  sme:             'SME',
  agent:           'Agent',
  corporate:       'Corporate',
  internal_wallet: 'Internal Wallet',
  partner:         'Partner',
  vas_provider:    'VAS Provider'
};

function ManageCustomers() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { confirm } = useDialog();
  const canRead             = hasPermission(user, 'customers.read');
  const canToggle           = hasPermission(user, 'customers.toggle');
  const canApproveHighRisk  = hasPermission(user, 'customers.approve_high_risk');
  const canAssessRisk       = hasPermission(user, 'customers.assess_risk');
  const canRunScreening     = hasPermission(user, 'screening.run');
  const canReviewScreening  = hasPermission(user, 'screening.review');
  const canCreateCase       = hasPermission(user, 'cases.create');
  const canReadTransactions = hasPermission(user, 'transactions.read');
  const canReadTmAlerts     = hasPermission(user, 'tm_alerts.read');
  const canReadReviews      = hasPermission(user, 'customer_reviews.read');
  const canUploadDoc        = hasPermission(user, 'customers.upload_document');
  const canDeleteDoc        = hasPermission(user, 'customers.delete_document');
  const canVerifyDoc        = hasPermission(user, 'customers.verify_document');
  const canCreate           = hasPermission(user, 'customers.create');
  const canExport           = hasPermission(user, 'customers.export');
  const canUpdate           = hasPermission(user, 'customers.update');
  const isPrivileged = isCustomerManager(user);

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [activeRisk, setActiveRisk] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eddFilter, setEddFilter] = useState('all');   // all | required | not_required
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedId, setSelectedId] = useState(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);   // customer details being edited
  const [detail, setDetail] = useState(null);
  const [screening, setScreening] = useState(null);   // { runs, latest, hits }
  const [screeningBusy, setScreeningBusy] = useState(false);
  // Related-module slices for the customer detail modal (each only fetched
  // if the viewer has the corresponding read permission).
  const [recentTx, setRecentTx]         = useState([]);
  const [openTmAlerts, setOpenTmAlerts] = useState([]);
  const [customerReviews, setCustomerReviews] = useState([]);
  const [documents, setDocuments]       = useState([]);
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [docUploadBusy, setDocUploadBusy] = useState(false);
  const [docFileError, setDocFileError] = useState('');
  const [docUploadForm, setDocUploadForm] = useState({
    file: null, document_type: 'id_copy', document_label: '', expires_at: ''
  });
  // In-page modal for status/committee decisions: { title, prompt, confirmLabel, danger, onConfirm }
  const [actionModal, setActionModal] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/customers/stats');
      if (res.data) setStats(res.data);
    } catch (e) { console.error('Customers stats error:', e.message); }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (searchTerm.trim()) params.set('query', searchTerm.trim());
      params.set('limit', '500');
      const res = await axios.get(`/customers?${params.toString()}`);
      setRows(Array.isArray(res.data) ? res.data : []);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (!canRead) return;
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  // The list follows the search box as you type (short debounce), so there
  // is no Search button. Also runs on first load and when filters change.
  useEffect(() => {
    if (!canRead) return undefined;
    const t = setTimeout(fetchList, 300);
    return () => clearTimeout(t);
  }, [canRead, fetchList]);

  // /customers?id=123 (from a notification or the list-match popup) opens
  // that client, then the address goes back to /customers.
  useEffect(() => {
    if (!canRead) return;
    const id = Number(new URLSearchParams(location.search).get('id'));
    if (Number.isInteger(id) && id > 0) {
      openDetail(id);
      navigate('/customers', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, canRead]);

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to access the Customers module.
            Ask your administrator for the <code>customers.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  // ---- client-side filter (table search + sort) ----
  const tsLc = tableSearch.trim().toLowerCase();
  // Type chips filter here (not on the server) so every chip keeps its count.
  // Type chips and the Status / Risk / EDD dropdowns filter the loaded list
  // here (not on the server) so every option keeps its count.
  const typeRows = rows.filter(r =>
    (activeType === 'all' || r.customer_type === activeType) &&
    (statusFilter === 'all' || r.status === statusFilter) &&
    (activeRisk === 'all' || r.risk_tier === activeRisk) &&
    (eddFilter === 'all' || (eddFilter === 'required' ? !!r.edd_required : !r.edd_required))
  );
  const countBy = (pred) => rows.filter(pred).length;
  const filtered = !tsLc ? typeRows : typeRows.filter(r => {
    const haystack = [
      r.external_reference,
      r.first_name, r.last_name, r.individual_id_number,
      r.legal_name, r.trading_name, r.registration_number,
      r.customer_type, r.risk_tier, r.status
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(tsLc);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const pageItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // ---- counts per type for chips ----
  const typeCounts = rows.reduce((acc, r) => {
    acc[r.customer_type] = (acc[r.customer_type] || 0) + 1;
    return acc;
  }, {});

  const displayName = (r) => {
    if (r.customer_type === 'individual') {
      return [r.first_name, r.last_name].filter(Boolean).join(' ') || '(unnamed)';
    }
    return r.trading_name || r.legal_name || '(unnamed business)';
  };

  // Branded report (utils/brandedPdf) of exactly the customers listed.
  const exportPDF = async () => {
    const list = filtered;
    if (!list.length) return;
    const cap = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : '');
    const filterParts = [
      searchTerm.trim() && `"${searchTerm.trim()}"`,
      tableSearch.trim() && `table: "${tableSearch.trim()}"`,
      activeType !== 'all' && TYPE_LABELS[activeType],
      statusFilter !== 'all' && cap(statusFilter),
      activeRisk !== 'all' && `${cap(activeRisk)} risk`,
      eddFilter !== 'all' && (eddFilter === 'required' ? 'EDD required' : 'No EDD')
    ].filter(Boolean);
    const RISK = { low: PDF_COLOURS.green, medium: PDF_COLOURS.orange, high: PDF_COLOURS.red };
    const STATUS = { active: PDF_COLOURS.green, pending: PDF_COLOURS.navy, suspended: PDF_COLOURS.orange,
                     closed: PDF_COLOURS.grey, rejected: PDF_COLOURS.red };

    await exportBrandedPdf({
      title: 'KNOW YOUR CUSTOMER (KYC) — CUSTOMER RISK REPORT',
      user,
      searchValue: filterParts.length ? filterParts.join('  ·  ') : 'All customers',
      fileName: 'KYC_Customer_Report',
      columns: [
        { header: 'Customer', width: 16, bold: true },
        { header: 'Type', width: 7 },
        { header: 'ID / Reg no.', width: 10 },
        { header: 'Risk Rating', width: 9 },
        { header: 'Status', width: 7 },
        { header: 'Product', width: 12 },
        { header: 'Delivery Channel', width: 9 },
        { header: 'PIP / Sanctions', width: 9 },
        { header: 'EDD', width: 8 },
        { header: 'Created', width: 10 }
      ],
      rows: list.map(r => [
        displayName(r) + (r.external_reference ? `\nRef: ${r.external_reference}` : ''),
        TYPE_LABELS[r.customer_type] || r.customer_type,
        r.customer_type === 'individual' ? r.individual_id_number : r.registration_number,
        r.risk_tier ? `${cap(r.risk_tier)}${r.risk_score != null ? ` (${r.risk_score})` : ''}` : 'Unscored',
        cap(r.status),
        r.product_type ? optionLabel(PRODUCT_TYPES, r.product_type) : '',
        r.delivery_channel ? optionLabel(DELIVERY_CHANNELS, r.delivery_channel) : '',
        [(r.is_pip || r.is_pep) && 'PIP', r.is_sanctioned && 'Sanctioned'].filter(Boolean).join(', ') || 'Clear',
        r.edd_required ? 'Required' : 'No',
        formatLastUpdated(r.created_at)
      ]),
      cellStyle: (row, col) => {
        const r = list[row];
        if (col === 3 && RISK[r.risk_tier]) return { textColor: RISK[r.risk_tier], fontStyle: 'bold' };
        if (col === 4 && STATUS[r.status]) return { textColor: STATUS[r.status], fontStyle: 'bold' };
        if (col === 7 && (r.is_pip || r.is_pep || r.is_sanctioned)) return { textColor: PDF_COLOURS.red, fontStyle: 'bold' };
        if (col === 8 && r.edd_required) return { textColor: PDF_COLOURS.red, fontStyle: 'bold' };
        return undefined;
      }
    });
  };

  const exportButtons = canExport && (
    <button className="export-button" onClick={exportPDF} disabled={!filtered.length}>Export PDF</button>
  );

  // Edit loads the full captured details first so the form starts from them.
  const openEdit = async (id) => {
    try {
      const res = await axios.get(`/customers/${id}`);
      setEditCustomer(res.data);
    } catch (e) {
      setError(e.message || 'Failed to load customer');
    }
  };

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setScreening(null);
    setRecentTx([]);
    setOpenTmAlerts([]);
    setCustomerReviews([]);
    setDocuments([]);
    try {
      // Always-fetched: the customer + their screening run
      const [det, scr] = await Promise.all([
        axios.get(`/customers/${id}`),
        axios.get(`/screening/results/${id}`).catch(() => ({ data: null }))
      ]);
      setDetail(det.data);
      if (scr.data) setScreening(scr.data);

      // Permission-gated extras. Failures are silent (we'd rather render the
      // modal without a section than block the whole detail view).
      const extras = [];
      if (canReadTransactions) {
        extras.push(axios.get(`/transactions?customer_id=${id}&limit=10`)
          .then(r => setRecentTx(Array.isArray(r.data) ? r.data : []))
          .catch(() => {}));
      }
      if (canReadTmAlerts) {
        extras.push(axios.get(`/tm-alerts?customer_id=${id}&disposition=pending&limit=20`)
          .then(r => setOpenTmAlerts(Array.isArray(r.data) ? r.data : []))
          .catch(() => {}));
      }
      if (canReadReviews) {
        extras.push(axios.get(`/customer-reviews?customer_id=${id}&limit=20`)
          .then(r => setCustomerReviews(Array.isArray(r.data) ? r.data : []))
          .catch(() => {}));
      }
      // KYC documents always pulled if user has customers.read (the page guard)
      extras.push(axios.get(`/customer-documents?customer_id=${id}&limit=50`)
        .then(r => setDocuments(Array.isArray(r.data) ? r.data : []))
        .catch(() => {}));
      if (extras.length) await Promise.all(extras);
    } catch (e) {
      setError(e.message || 'Failed to load customer');
      setSelectedId(null);
    }
  };

  const reassessRisk = async () => {
    if (!detail) return;
    try {
      await axios.post(`/customers/${detail.id}/assess-risk`, { assessment_type: 'manual' });
      const res = await axios.get(`/customers/${detail.id}`);
      setDetail(res.data);
      fetchList();
      fetchStats();
    } catch (e) {
      setError(e.message || 'Re-assessment failed');
    }
  };

  const rescreenCustomer = async () => {
    if (!detail) return;
    try {
      setScreeningBusy(true);
      await axios.post(`/screening/run/${detail.id}`, { run_type: 'manual' });
      const [det, scr] = await Promise.all([
        axios.get(`/customers/${detail.id}`),
        axios.get(`/screening/results/${detail.id}`)
      ]);
      setDetail(det.data);
      setScreening(scr.data);
      fetchList();
      fetchStats();
    } catch (e) {
      setError(e.message || 'Re-screen failed');
    } finally {
      setScreeningBusy(false);
    }
  };

  // Reload the customer (risk rating, flags, audit trail) after an action
  // that changes them from another panel.
  const refreshDetail = async () => {
    if (!detail) return;
    try {
      const det = await axios.get(`/customers/${detail.id}`);
      setDetail(det.data);
    } catch (_) { /* keep what is shown */ }
  };

  const setHitDisposition = async (hitId, disposition) => {
    try {
      await axios.post(`/screening/hits/${hitId}/disposition`, { disposition });
      // The review re-rates the customer and adds to the audit trail
      const scr = await axios.get(`/screening/results/${detail.id}`);
      setScreening(scr.data);
      await refreshDetail();
      fetchList();
      fetchStats();
    } catch (e) {
      setError(e.message || 'Disposition update failed');
    }
  };

  // Promote a screening hit into a formal case (idempotent on the server —
  // re-clicking on a hit that already has a case just returns the same case).
  // Hits that scored >= 90 are already auto-promoted by the screening run.
  const promoteHitToCase = async (hitId) => {
    try {
      const res = await axios.post('/cases/promote', { hit_id: hitId });
      const caseId = res?.data?.id;
      if (caseId) navigate(`/cases?id=${caseId}`);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Promote to case failed');
    }
  };

  // ---- KYC documents ----
  const refreshDocuments = async () => {
    if (!detail) return;
    try {
      const r = await axios.get(`/customer-documents?customer_id=${detail.id}&limit=50`);
      setDocuments(Array.isArray(r.data) ? r.data : []);
    } catch (_) { /* keep existing list */ }
    await refreshDetail(); // document changes are in the audit trail
  };

  const uploadDocument = async (e) => {
    e.preventDefault();
    if (!detail || !docUploadForm.file) {
      setError('Please choose a file to upload');
      return;
    }
    setDocUploadBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', docUploadForm.file);
      fd.append('document_type',  docUploadForm.document_type);
      if (docUploadForm.document_label) fd.append('document_label', docUploadForm.document_label);
      if (docUploadForm.expires_at)     fd.append('expires_at', docUploadForm.expires_at);
      await axios.post(`/customer-documents/${detail.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocUploadOpen(false);
      setDocUploadForm({ file: null, document_type: 'id_copy', document_label: '', expires_at: '' });
      await refreshDocuments();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Upload failed');
    } finally {
      setDocUploadBusy(false);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await axios.get(`/customer-documents/${doc.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `document-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Download failed');
    }
  };

  // Opens the document in a new tab. The tab is opened inside the click so
  // pop-up blockers allow it, then pointed at the file once it has loaded.
  const viewDocument = async (doc) => {
    const tab = window.open('', '_blank');
    try {
      const res = await axios.get(`/customer-documents/${doc.id}/download?inline=1`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.mime_type || 'application/pdf' }));
      if (tab) tab.location.href = url; else window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (tab) tab.close();
      setError(e.response?.data?.error?.message || e.message || 'Could not open the document');
    }
  };

  // PDF only, 10 MB max (the server enforces the same).
  const MAX_DOC_BYTES = 10 * 1024 * 1024;
  const pickDocFile = (file) => {
    setDocFileError('');
    if (!file) { setDocUploadForm(f => ({ ...f, file: null })); return; }
    const isPdf = /\.pdf$/i.test(file.name) && (!file.type || file.type === 'application/pdf');
    if (!isPdf) {
      setDocFileError('Only PDF files can be uploaded.');
      setDocUploadForm(f => ({ ...f, file: null }));
      return;
    }
    if (file.size > MAX_DOC_BYTES) {
      setDocFileError(`The file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the maximum is 10 MB.`);
      setDocUploadForm(f => ({ ...f, file: null }));
      return;
    }
    setDocUploadForm(f => ({ ...f, file }));
  };

  const verifyDocument = async (docId, status) => {
    try {
      await axios.post(`/customer-documents/${docId}/verify`, { status });
      await refreshDocuments();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Verification failed');
    }
  };

  const deleteDocument = async (doc) => {
    const ok = await confirm({
      title: 'Delete document',
      message: `Delete document "${doc.file_name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await axios.delete(`/customer-documents/${doc.id}`);
      await refreshDocuments();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Delete failed');
    }
  };

  // ---- Status transitions ----
  // Open the in-page confirm modal; the caller supplies the API call.
  const openActionModal = (cfg) => {
    setActionReason('');
    setActionError('');
    setActionModal(cfg);
  };

  const runAction = async () => {
    if (!actionModal || !actionModal.onConfirm) return;
    // Every status change must say why (kept in the audit trail).
    const reason = actionReason.trim();
    if (!reason) { setActionError('A reason is required.'); return; }
    try {
      setActionBusy(true);
      setActionError('');
      await actionModal.onConfirm(reason);
      // Refresh the detail + list after a successful action.
      if (detail) {
        const det = await axios.get(`/customers/${detail.id}`);
        setDetail(det.data);
      }
      fetchList();
      fetchStats();
      setActionModal(null);
    } catch (e) {
      setActionError(e.message || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const changeStatus = (status, title) => {
    if (!detail) return;
    openActionModal({
      title: title || `Change status to ${status}`,
      promptLabel: 'Reason',
      confirmLabel: 'Confirm',
      danger: status === 'rejected' || status === 'closed' || status === 'suspended',
      onConfirm: async (reason) => {
        await axios.patch(`/customers/${detail.id}/status`, { status, reason });
      }
    });
  };

  const committeeDecision = (decision) => {
    if (!detail) return;
    openActionModal({
      title: decision === 'approve'
        ? 'Approve this customer?'
        : 'Reject this customer?',
      promptLabel: 'Reason',
      confirmLabel: decision === 'approve' ? 'Approve' : 'Reject',
      danger: decision !== 'approve',
      onConfirm: async (notes) => {
        await axios.post(`/customers/${detail.id}/high-risk-approval`, { decision, notes });
      }
    });
  };

  return (
    <div className="page-container">
      {/* Stat cards — same look as UN Sanctions / PIPs */}
      {user && (
        <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): lead card on the left, the rest compact beside it */}
          <div className="stats-layout">
            <div className="stat-card stats-layout__lead stat-card--teal">
              <div className="stat-label">Source</div>
              <div className="stat-value stat-value--teal" style={{ fontSize: '1rem' }}>Customer Onboarding</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)', marginTop: '0.35rem' }}>
                Last updated: <strong>{formatLastUpdated(stats?.last_updated)}</strong>
              </div>
            </div>
            <div className="stats-layout__rest">
          <div className="stats-grid">
            {[
              // [label, value, card colour, value colour, tooltip]
              ['Total Customers', stats?.total_customers, 'blue', 'blue', 'All customers of your organisation'],
              ['Individuals', stats?.by_type?.individual || 0, 'purple', 'purple', 'Clients (natural persons)'],
              ['Legal Persons', stats ? Math.max(0, (stats.total_customers || 0) - (stats.by_type?.individual || 0)) : undefined,
                'teal', 'teal', 'SMEs, corporates, partners, agents, VAS providers and internal wallets'],
              ['New (30 days)', stats?.new_last_30d, 'blue', 'blue', 'Onboarded in the last 30 days'],
              ['Pending Approval', stats?.by_status?.pending || 0, 'orange', 'orange', 'Waiting for approval before they can transact'],
              ['Active', stats?.by_status?.active || 0, 'teal', 'green', 'Approved and active'],
              ['High Risk', stats?.by_risk?.high || 0, 'orange', 'red', 'Overall risk rating High'],
              ['Medium Risk', stats?.by_risk?.medium || 0, 'orange', 'orange', 'Overall risk rating Medium'],
              ['Low Risk', stats?.by_risk?.low || 0, 'teal', 'green', 'Overall risk rating Low'],
              ['EDD Outstanding', stats?.edd_open, 'purple', 'red', 'Enhanced Due Diligence required and not yet completed'],
              ['PIP Flagged', stats?.pip_flagged, 'purple', 'purple', 'Customers flagged as a PIP / PEP'],
              ['Sanctions Flagged', stats?.sanctioned, 'brown', 'red', 'Customers flagged on a sanctions list'],
              ['Hits to Review', stats?.screening_hits_pending, 'orange', 'orange', 'Screening hits waiting for a decision'],
              ['Reviews Due (30 days)', stats?.reviews_due_30d, 'blue', 'blue', 'Periodic reviews due within 30 days or overdue']
            ].map(([label, value, card, colour, tip]) => (
              <div key={label} className={`stat-card stat-card--${card}`} title={tip}>
                <div className="stat-label">{label}</div>
                <div className={`stat-value stat-value--${colour}`}>{value ?? '—'}</div>
              </div>
            ))}
          </div>
        </div>
          </div>
        </div>
      )}

      {/* Admin search bar */}
      {isPrivileged && (
        <div style={{ marginBottom: '2rem' }}>
          <div className="table-controls">
            {/* Search box with its clear (×) on the right, inside the box */}
            <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: '300px' }}>
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-filter"
                style={{ width: '100%', maxWidth: 'none', paddingRight: '2.25rem', boxSizing: 'border-box' }}
              />
              {searchTerm && (
                <span
                  role="button"
                  tabIndex={0}
                  title="Clear search"
                  aria-label="Clear search"
                  onClick={() => setSearchTerm('')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSearchTerm(''); }}
                  style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-secondary, #607d8b)', display: 'flex' }}
                >
                  <FaTimes />
                </span>
              )}
            </div>
            <div className="button-group">
              {canCreate && (
                <>
                  <button className="export-button" onClick={() => setShowAddCustomer(true)}>
                    <FaPlus style={{ marginRight: 5 }} /> Add Customer
                  </button>
                  <button className="export-button" onClick={() => setShowBulkUpload(true)}>
                    <FaUpload style={{ marginRight: 5 }} /> Add Bulk Customers
                  </button>
                </>
              )}
              {exportButtons}
            </div>
          </div>
        </div>
      )}

      {/* Search hero for users without customers.create — list-only. */}
      {!isPrivileged && (
        <div className="search-hero">
          <h1>KYC</h1>
          <p>Search and review existing customers.</p>
          <div className="search-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, ID, registration number…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && <FaTimes className="clear-icon" onClick={() => setSearchTerm('')} />}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert-banner alert-banner--danger">
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="search-results-info" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div className="search-count" style={{ flex: 1, fontSize: '0.8rem' }}>
              Showing {filtered.length} of {rows.length} customer{rows.length === 1 ? '' : 's'}
            </div>
            {/* The admin search bar carries these; the search-hero layout has no button row. */}
            {!isPrivileged && exportButtons && <div className="button-group">{exportButtons}</div>}
            <input
              type="text"
              className="table-search-input"
              placeholder="Search table..."
              value={tableSearch}
              onChange={e => { setTableSearch(e.target.value); setCurrentPage(1); }}
            />
            <select
              className="table-search-input"
              style={{ width: 'auto' }}
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              title="Filter by status"
            >
              <option value="all">All statuses</option>
              {['pending', 'active', 'suspended', 'closed', 'rejected'].map(st => (
                <option key={st} value={st}>
                  {st.charAt(0).toUpperCase() + st.slice(1)} ({countBy(r => r.status === st)})
                </option>
              ))}
            </select>
            <select
              className="table-search-input"
              style={{ width: 'auto' }}
              value={activeRisk}
              onChange={e => { setActiveRisk(e.target.value); setCurrentPage(1); }}
              title="Filter by risk rating"
            >
              <option value="all">All risk ratings</option>
              {['low', 'medium', 'high'].map(t => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)} risk ({countBy(r => r.risk_tier === t)})
                </option>
              ))}
            </select>
            <select
              className="table-search-input"
              style={{ width: 'auto' }}
              value={eddFilter}
              onChange={e => { setEddFilter(e.target.value); setCurrentPage(1); }}
              title="Filter by Enhanced Due Diligence (EDD)"
            >
              <option value="all">EDD: all</option>
              <option value="required">EDD required ({countBy(r => !!r.edd_required)})</option>
              <option value="not_required">No EDD ({countBy(r => !r.edd_required)})</option>
            </select>
            <span className="un-results__split">
              {CUSTOMER_TYPES.map(t => (typeCounts[t] > 0) && (
                <button
                  key={t}
                  type="button"
                  className={`un-results__split-btn${activeType === t ? ' un-results__split-btn--active' : ''}`}
                  onClick={() => { setActiveType(activeType === t ? 'all' : t); setCurrentPage(1); }}
                >
                  {t === 'individual' ? <FaUser /> : <FaBuilding />} {TYPE_LABELS[t]} {typeCounts[t]}
                </button>
              ))}
            </span>
          </div>

          <div className="un-card-list">
            {pageItems.map((r, i) => {
              const isInd = r.customer_type === 'individual';
              const isAlt = i % 2 === 1;
              return (
                <div key={r.id} className={`un-card un-card--${isInd ? 'individual' : 'entity'}${isAlt ? ' un-card--alt' : ''}`}>
                  <div className="un-card__header">
                    <div className="un-card__title-row">
                      <span className={`un-card__badge un-card__badge--${isInd ? 'individual' : 'entity'}`}>
                        {isInd ? <FaUser /> : <FaBuilding />} {TYPE_LABELS[r.customer_type]}
                      </span>
                      <h3 className="un-card__title">{displayName(r)}</h3>
                    </div>
                    <div className="un-card__meta">
                      <span className={`un-meta__pill cust-pill--risk-${r.risk_tier || 'unknown'}`}>
                        <FaShieldAlt /> Risk: {r.risk_tier || 'unscored'}{r.risk_score ? ` (${r.risk_score})` : ''}
                      </span>
                      <span className="un-meta__pill">KYC: {r.kyc_tier}</span>
                      <span className={`un-meta__pill cust-pill--status-${r.status}`}>Status: {r.status}</span>
                      {r.is_pep && <span className="un-meta__pill cust-pill--flag">PEP</span>}
                      {r.is_pip && <span className="un-meta__pill cust-pill--flag">PIP hit</span>}
                      {r.is_sanctioned && <span className="un-meta__pill cust-pill--flag">Sanctioned</span>}
                      {r.edd_required && <span className="un-meta__pill cust-pill--flag">Enhanced Due Diligence (EDD) required</span>}
                      {!r.is_pep && !r.is_pip && !r.is_sanctioned && (
                        <span className="un-meta__pill cust-pill--status-active">Screened · clear</span>
                      )}
                      {r.external_reference && <span className="un-meta__pill">Ref: {r.external_reference}</span>}
                    </div>
                  </div>
                  {/* Quick view — key facts, each label directly above its value */}
                  <div className="un-card__body cust-quickview">
                    {(isInd
                      ? [
                          ['ID Number', r.individual_id_number],
                          ['Nationality', [
                            r.nationality_category && optionLabel(NATIONALITY_CATEGORIES, r.nationality_category),
                            r.nationality
                          ].filter(Boolean).join(' — ')],
                          ['Occupation', r.employment_status && optionLabel(OCCUPATIONS, r.employment_status)]
                        ]
                      : [
                          ['Reg #', r.registration_number],
                          ['Institution', r.business_type && optionLabel(INSTITUTION_TYPES, r.business_type)],
                          ['Nature of Business', r.nature_of_business && optionLabel(NATURE_OF_BUSINESS, r.nature_of_business)]
                        ]
                    ).concat([
                      ['Product', r.product_type && optionLabel(PRODUCT_TYPES, r.product_type)],
                      ['Delivery channel', r.delivery_channel && optionLabel(DELIVERY_CHANNELS, r.delivery_channel)],
                      ['Created', formatLastUpdated(r.created_at)]
                    ]).map(([label, value]) => (
                      <div className="cust-detail-field cust-quickview__field" key={label}>
                        <span className="cust-detail-label">{label}</span>
                        <span className="cust-detail-value">{value || '—'}</span>
                      </div>
                    ))}
                  </div>
                  <div className="un-card__footer">
                    {canUpdate && r.status !== 'closed' && r.status !== 'rejected' && (
                      <button type="button" className="btn-cancel" style={{ marginRight: '0.5rem' }} onClick={() => openEdit(r.id)}>Edit</button>
                    )}
                    <button type="button" className="view-details-btn" onClick={() => openDetail(r.id)}>View details</button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pagination-controls">
            <div className="pagination-left">
              <span className="pagination-info">
                {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
              </span>
            </div>
            <div className="pagination-right">
              <div className="pagination-per-page">
                <span>Per page:</span>
                <select value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="pagination pagination-pages">
                <button onClick={() => setCurrentPage(c => Math.max(1, c - 1))} disabled={currentPage === 1}>← Prev</button>
                <span className="pagination-info">Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))} disabled={currentPage === totalPages}>Next →</button>
              </div>
            </div>
          </div>
        </>
      )}

      {selectedId && (
        <div className="modal-overlay" onClick={() => setSelectedId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Customer #{selectedId}</h2>
              <button className="modal-close" onClick={() => setSelectedId(null)}>×</button>
            </div>
            <div className="modal-body">
              {!detail ? <p>Loading…</p> : (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  <div className="un-card__meta" style={{ flex: 1, margin: 0 }}>
                    <span className="un-meta__pill">Type: {TYPE_LABELS[detail.customer_type]}</span>
                    <span className={`un-meta__pill cust-pill--risk-${detail.risk_tier}`}>Risk: {detail.risk_tier} ({detail.risk_score})</span>
                    <span className="un-meta__pill">KYC: {detail.kyc_tier}</span>
                    {detail.product_type && <span className="un-meta__pill">Product: {optionLabel(PRODUCT_TYPES, detail.product_type)}</span>}
                    {detail.delivery_channel && <span className="un-meta__pill">Channel: {optionLabel(DELIVERY_CHANNELS, detail.delivery_channel)}</span>}
                    <span className={`un-meta__pill cust-pill--status-${detail.status}`}>Status: {detail.status}</span>
                    {detail.edd_required && <span className="un-meta__pill cust-pill--flag">Enhanced Due Diligence (EDD) required</span>}
                  </div>
                  {/* Onboarding decision for pending customers. EDD cases need the
                      High-Risk Committee permission; others need customers.toggle. */}
                  {detail.status === 'pending' && (detail.edd_required ? canApproveHighRisk : canToggle) && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <button
                        className="export-button"
                        onClick={() => (detail.edd_required
                          ? committeeDecision('approve')
                          : changeStatus('active', 'Approve this customer?'))}
                      >
                        Approve
                      </button>
                      <button
                        className="btn-cancel"
                        onClick={() => (detail.edd_required
                          ? committeeDecision('reject')
                          : changeStatus('rejected', 'Reject this customer?'))}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  {/* Status actions for active / suspended customers */}
                  {detail.status === 'active' && canToggle && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <button className="btn-cancel" onClick={() => changeStatus('suspended', 'Suspend this customer?')}>
                        Suspend
                      </button>
                      <button className="btn-cancel" onClick={() => changeStatus('closed', 'Close this customer record?')}>
                        Close
                      </button>
                    </div>
                  )}
                  {detail.status === 'suspended' && canToggle && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <button className="export-button" onClick={() => changeStatus('active', 'Re-activate this customer?')}>
                        Re-activate
                      </button>
                      <button className="btn-cancel" onClick={() => changeStatus('closed', 'Close this customer record?')}>
                        Close
                      </button>
                    </div>
                  )}
                  {(detail.status === 'closed' || detail.status === 'rejected') && canToggle && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <button className="export-button" onClick={() => changeStatus('pending', 'Re-open this customer?')}>
                        Re-open
                      </button>
                    </div>
                  )}
                  </div>

                  {detail.individual && (
                    <section className="form-section">
                      <header className="form-section__head">
                        <h4 className="form-section__title">Individual</h4>
                      </header>
                      <div className="cust-detail-grid">
                        <div className="cust-detail-field"><span className="cust-detail-label">Full name</span><span className="cust-detail-value">{[detail.individual.first_name, detail.individual.middle_name].filter(Boolean).join(' ')}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Surname</span><span className="cust-detail-value">{detail.individual.last_name}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Maiden name</span><span className="cust-detail-value">{detail.individual.maiden_name || '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">DOB</span><span className="cust-detail-value">{formatDate(detail.individual.date_of_birth)}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Nationality</span><span className="cust-detail-value">{[detail.individual.nationality_category && optionLabel(NATIONALITY_CATEGORIES, detail.individual.nationality_category), detail.individual.nationality].filter(Boolean).join(' — ') || '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Residence</span><span className="cust-detail-value">{detail.individual.country_of_residence || '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">ID Type</span><span className="cust-detail-value">{detail.individual.id_type}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">ID Number</span><span className="cust-detail-value">{detail.individual.id_number}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">PIP</span><span className="cust-detail-value">{detail.individual.pip_declared ? 'Yes' : 'No'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Occupation</span><span className="cust-detail-value">{detail.individual.employment_status ? optionLabel(OCCUPATIONS, detail.individual.employment_status) : '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Source of Income</span><span className="cust-detail-value">{detail.individual.source_of_funds ? optionLabel(SOURCES_OF_INCOME, detail.individual.source_of_funds) : '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Source of Funds for the Transaction</span><span className="cust-detail-value">{detail.individual.transaction_source_of_funds ? optionLabel(TRANSACTION_SOURCES_OF_FUNDS, detail.individual.transaction_source_of_funds) : '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Associate of a PIP</span><span className="cust-detail-value">{detail.individual.pip_associate_relationship ? optionLabel(PIP_ASSOCIATE_OPTIONS, detail.individual.pip_associate_relationship) : (detail.individual.is_pip_associate ? 'Yes' : 'No')}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Income band</span><span className="cust-detail-value">{detail.individual.monthly_income_band || '—'}</span></div>
                      </div>
                    </section>
                  )}

                  {/* PIP check — records in the PIP database accepted / rejected at capture or edit */}
                  {detail.individual && Array.isArray(detail.pip_matches) && detail.pip_matches.length > 0 && (
                    <section className="form-section">
                      <header className="form-section__head">
                        <h4 className="form-section__title">PIP Check</h4>
                        <p className="form-section__subtitle">
                          Possible PIP and Associate of a PIP records found in the PIP database, and the decision on each.
                        </p>
                      </header>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-color,#e3e6ea)', textAlign: 'left' }}>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Type</th>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Record</th>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Score</th>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Decision</th>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Decided</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.pip_matches.map(m => (
                              <tr key={`${m.source}:${m.record_id}`} style={{ borderBottom: '1px solid var(--border-color,#eef0f3)' }}>
                                <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                                  <span className="un-meta__pill" style={{ fontSize: '0.7rem' }}>
                                    {m.source === 'pip' ? 'PIP' : 'Associate of a PIP'}
                                  </span>
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  <div><strong>{m.matched_name}</strong></div>
                                  {m.source === 'associate' && (
                                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary,#607d8b)' }}>
                                      {m.relationship_type || 'Associate'} of {m.related_pip_name}
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '0.5rem' }}>{Number(m.match_score)}</td>
                                <td style={{ padding: '0.5rem' }}>
                                  <span className={`un-meta__pill ${m.decision === 'accepted' ? 'cust-pill--risk-high' : 'cust-pill--status-closed'}`}>
                                    {m.decision === 'accepted' ? 'Accepted' : 'Rejected'}
                                  </span>
                                </td>
                                <td style={{ padding: '0.5rem', fontSize: '0.78rem' }}>
                                  {formatLastUpdated(m.decided_at)}{m.decided_by_name ? ` by ${m.decided_by_name}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  )}

                  {detail.business && (
                    <section className="form-section">
                      <header className="form-section__head">
                        <h4 className="form-section__title">Business</h4>
                      </header>
                      <div className="cust-detail-grid">
                        <div className="cust-detail-field"><span className="cust-detail-label">Legal name</span><span className="cust-detail-value">{detail.business.legal_name}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Trading name</span><span className="cust-detail-value">{detail.business.trading_name || '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Reg #</span><span className="cust-detail-value">{detail.business.registration_number || '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Tax #</span><span className="cust-detail-value">{detail.business.tax_number || '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Institution</span><span className="cust-detail-value">{detail.business.business_type ? optionLabel(INSTITUTION_TYPES, detail.business.business_type) : '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Nature of business</span><span className="cust-detail-value">{detail.business.nature_of_business ? optionLabel(NATURE_OF_BUSINESS, detail.business.nature_of_business) : '—'}</span></div>
                        <div className="cust-detail-field"><span className="cust-detail-label">Country</span><span className="cust-detail-value">{detail.business.country_of_incorporation || '—'}</span></div>
                      </div>
                      {detail.business.directors?.length > 0 && (
                        <p style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                          <strong>Directors ({detail.business.directors.length}):</strong>{' '}
                          {detail.business.directors.map(d => `${d.first_name} ${d.last_name}`).join('; ')}
                        </p>
                      )}
                      {detail.business.shareholders?.length > 0 && (
                        <p style={{ fontSize: '0.85rem' }}>
                          <strong>Shareholders ({detail.business.shareholders.length}):</strong>{' '}
                          {detail.business.shareholders.map(s => `${s.full_name} (${s.percentage ?? '—'}%)`).join('; ')}
                        </p>
                      )}
                      {detail.business.ubos?.length > 0 && (
                        <p style={{ fontSize: '0.85rem' }}>
                          <strong>UBOs ({detail.business.ubos.length}):</strong>{' '}
                          {detail.business.ubos.map(u => `${u.first_name} ${u.last_name} (${u.beneficial_interest_pct ?? '—'}%, ${optionLabel(BO_PIP_OPTIONS, u.pip_status || (u.is_pep_flag ? 'yes' : 'no'))}, ${[u.nationality_category && optionLabel(NATIONALITY_CATEGORIES, u.nationality_category), u.nationality].filter(Boolean).join(' — ') || 'nationality unknown'})`).join('; ')}
                        </p>
                      )}
                    </section>
                  )}

                  {/* Screening — hits against PIPs + UN Sanctions */}
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Screening</h4>
                      <p className="form-section__subtitle">
                        {screening?.latest
                          ? `Last run ${formatLastUpdated(screening.latest.started_at)} — ${screening.latest.total_hits} hit${screening.latest.total_hits === 1 ? '' : 's'}`
                          : 'No screening run yet.'}
                      </p>
                    </header>

                    {screening?.hits && screening.hits.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-color,#e3e6ea)', textAlign: 'left' }}>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Source</th>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Matched</th>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Score</th>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Disposition</th>
                              <th style={{ padding: '0.45rem 0.5rem' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {screening.hits.map(h => (
                              <tr key={h.id} style={{ borderBottom: '1px solid var(--border-color,#eef0f3)' }}>
                                <td style={{ padding: '0.5rem' }}>
                                  <span className="un-meta__pill" style={{ fontSize: '0.7rem' }}>
                                    {h.source.replace(/_/g, ' ')}
                                  </span>
                                  {h.source_reference && (
                                    <span style={{ marginLeft: '0.4rem', color: 'var(--text-secondary,#607d8b)', fontSize: '0.75rem' }}>
                                      {h.source_reference}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  <div><strong>{h.matched_name}</strong></div>
                                  {h.matched_alias && (
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)' }}>
                                      via alias: {h.matched_alias}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary,#607d8b)' }}>
                                    matched on {h.customer_field}
                                  </div>
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  <span className={`un-meta__pill ${h.match_score >= 90 ? 'cust-pill--risk-high' : h.match_score >= 70 ? 'cust-pill--risk-medium' : 'cust-pill--risk-low'}`}>
                                    {h.match_score} · {h.match_type}
                                  </span>
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  <span className={`un-meta__pill cust-pill--status-${h.disposition === 'false_positive' ? 'closed' : h.disposition === 'confirmed' ? 'rejected' : 'pending'}`}>
                                    {h.disposition}
                                  </span>
                                </td>
                                <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                                  {!canReviewScreening ? (
                                    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary,#607d8b)' }}>—</span>
                                  ) : h.disposition === 'pending' ? (
                                    <>
                                      <button
                                        type="button"
                                        className="btn-cancel"
                                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginRight: '0.25rem' }}
                                        onClick={() => setHitDisposition(h.id, 'false_positive')}
                                      >
                                        False positive
                                      </button>
                                      <button
                                        type="button"
                                        className="export-button"
                                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                                        onClick={() => setHitDisposition(h.id, 'confirmed')}
                                      >
                                        Confirm
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn-cancel"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                                      onClick={() => setHitDisposition(h.id, 'pending')}
                                    >
                                      Reopen
                                    </button>
                                  )}
                                  {/* Case linkage — either jump to the auto-created case or promote on demand. */}
                                  {h.case_id ? (
                                    <button
                                      type="button"
                                      className="btn-cancel"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginLeft: '0.25rem' }}
                                      onClick={() => navigate(`/cases?id=${h.case_id}`)}
                                      title={`Open ${h.case_number}`}
                                    >
                                      <FaFolderOpen style={{ marginRight: '0.25rem' }} />
                                      {h.case_number || 'Open case'}
                                    </button>
                                  ) : canCreateCase ? (
                                    <button
                                      type="button"
                                      className="btn-cancel"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginLeft: '0.25rem' }}
                                      onClick={() => promoteHitToCase(h.id)}
                                      title="Open a case for this hit"
                                    >
                                      <FaFolderOpen style={{ marginRight: '0.25rem' }} />
                                      Promote to case
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary,#607d8b)' }}>
                        {screening?.latest
                          ? 'No matches found against any reference list.'
                          : 'Run a screening scan to check this customer against PIPs and UN Sanctions.'}
                      </p>
                    )}

                    {canRunScreening && (
                      <button
                        className={`export-button${screeningBusy ? ' is-loading' : ''}`}
                        style={{ marginTop: '0.85rem' }}
                        onClick={rescreenCustomer}
                        disabled={screeningBusy}
                      >
                        {screeningBusy ? 'Screening…' : 'Re-screen now'}
                      </button>
                    )}
                  </section>

                  {detail.latest_risk_assessment && (
                    <section className="form-section">
                      <header className="form-section__head">
                        <h4 className="form-section__title">Latest Risk Assessment</h4>
                      </header>
                      <p style={{ fontSize: '0.8rem', margin: '0 0 0.75rem', color: 'var(--text-secondary, #607d8b)' }}>
                        Assessed {formatLastUpdated(detail.latest_risk_assessment.assessed_at)}
                      </p>
                      <RiskAssessmentView assessment={detail.latest_risk_assessment} />
                      {canAssessRisk && (
                        <button className="export-button" style={{ marginTop: '0.85rem' }} onClick={reassessRisk}>
                          Re-assess Risk
                        </button>
                      )}
                    </section>
                  )}

                  {/* Status & Lifecycle — permission-gated transition buttons */}
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Status & Lifecycle</h4>
                      <p className="form-section__subtitle">
                        Current status: <strong>{detail.status}</strong>
                        {detail.edd_required && ' · Enhanced Due Diligence (EDD) required'}
                        {detail.high_risk_approved_at && ` · Committee decided ${formatLastUpdated(detail.high_risk_approved_at)}`}
                      </p>
                    </header>

                    {/* No permission at all — read-only explainer */}
                    {!canToggle && !canApproveHighRisk && (
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary,#607d8b)' }}>
                        You don't have permission to change this customer's status. Ask an administrator for
                        the <code>customers.toggle</code> or <code>customers.approve_high_risk</code> permission.
                      </p>
                    )}

                    {/* PENDING — needs review */}
                    {/* PENDING — decided with Approve / Reject at the top of this window */}
                    {detail.status === 'pending' && (
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary,#607d8b)' }}>
                        {detail.edd_required
                          ? (canApproveHighRisk
                              ? 'Approve or reject this customer with the buttons at the top.'
                              : <>This customer requires High-Risk Committee approval. You need the <code>customers.approve_high_risk</code> permission.</>)
                          : (canToggle
                              ? 'Approve or reject this customer with the buttons at the top.'
                              : <>You need the <code>customers.toggle</code> permission to approve or reject this customer.</>)}
                      </p>
                    )}

                    {/* ACTIVE / SUSPENDED — actions are at the top of this window */}
                    {(detail.status === 'active' || detail.status === 'suspended') && (canToggle || canApproveHighRisk) && (
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary,#607d8b)' }}>
                        {canToggle
                          ? (detail.status === 'active'
                              ? 'Suspend or close this customer with the buttons at the top.'
                              : 'Re-activate or close this customer with the buttons at the top.')
                          : <>You need the <code>customers.toggle</code> permission to change this customer's status.</>}
                      </p>
                    )}

                    {/* CLOSED / REJECTED — can only be re-opened (back to pending for review) */}
                    {(detail.status === 'closed' || detail.status === 'rejected') && (
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary,#607d8b)' }}>
                        This customer is {detail.status}.{' '}
                        {canToggle
                          ? 'Re-open it with the button at the top; it goes back to pending for a new review.'
                          : <>You need the <code>customers.toggle</code> permission to re-open it.</>}
                      </p>
                    )}
                  </section>

                  {/* Periodic CDD review status */}
                  {canReadReviews && (
                    <section className="form-section" style={{ marginTop: '1rem' }}>
                      <header className="form-section__head">
                        <h4 className="form-section__title">Periodic CDD review</h4>
                        <p className="form-section__subtitle">
                          Next review due: {detail.next_review_due ? formatLastUpdated(detail.next_review_due) : '—'}
                          {detail.last_reviewed_at ? ` · Last reviewed ${formatLastUpdated(detail.last_reviewed_at)}` : ''}
                        </p>
                      </header>
                      {customerReviews.length === 0 ? (
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary,#607d8b)' }}>
                          No reviews scheduled or completed yet.
                        </p>
                      ) : (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {customerReviews.slice(0, 5).map(rv => (
                            <li key={rv.id}
                                style={{
                                  padding: '0.4rem 0',
                                  borderBottom: '1px solid var(--border-color,#eef0f3)',
                                  display: 'flex', justifyContent: 'space-between', gap: '1rem',
                                  fontSize: '0.85rem', cursor: 'pointer'
                                }}
                                onClick={() => navigate(`/customer-reviews`)}>
                              <span>Due {formatLastUpdated(rv.due_at)} · {rv.risk_tier_at_due || 'low'} risk</span>
                              <span style={{ color: rv.status === 'open' ? '#0d47a1' : 'var(--text-secondary)' }}>
                                {rv.status}{rv.completed_at ? ` (${formatLastUpdated(rv.completed_at)})` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}

                  {/* Pending TM alerts */}
                  {canReadTmAlerts && openTmAlerts.length > 0 && (
                    <section className="form-section" style={{ marginTop: '1rem' }}>
                      <header className="form-section__head">
                        <h4 className="form-section__title">Pending TM alerts ({openTmAlerts.length})</h4>
                        <p className="form-section__subtitle">Transaction Monitoring alerts awaiting review.</p>
                      </header>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {openTmAlerts.slice(0, 5).map(a => (
                          <li key={a.id}
                              style={{
                                padding: '0.5rem 0', borderBottom: '1px solid var(--border-color,#eef0f3)',
                                display: 'flex', justifyContent: 'space-between', gap: '1rem',
                                fontSize: '0.85rem', cursor: 'pointer'
                              }}
                              onClick={() => navigate(`/tm-alerts`)}>
                            <span><strong>{a.rule_name}</strong> · {a.severity}</span>
                            <span style={{ color: 'var(--text-secondary)' }}>score {a.score}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {/* Recent transactions */}
                  {canReadTransactions && (
                    <section className="form-section" style={{ marginTop: '1rem' }}>
                      <header className="form-section__head">
                        <h4 className="form-section__title">Recent transactions</h4>
                        <p className="form-section__subtitle">Most recent 10 records · auto-screened by TM rule engine.</p>
                      </header>
                      {recentTx.length === 0 ? (
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary,#607d8b)' }}>
                          No transactions recorded for this customer.
                        </p>
                      ) : (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {recentTx.map(t => (
                            <li key={t.id}
                                style={{
                                  padding: '0.5rem 0', borderBottom: '1px solid var(--border-color,#eef0f3)',
                                  display: 'flex', justifyContent: 'space-between', gap: '1rem',
                                  fontSize: '0.85rem'
                                }}>
                              <span>{formatLastUpdated(t.occurred_at)} · {t.direction} · {t.channel || '—'}</span>
                              <span>
                                <strong>{t.currency || 'NAD'} {Number(t.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                                {t.alert_count > 0 && (
                                  <span style={{ marginLeft: '0.5rem', color: '#c62828' }}>· {t.alert_count} alert{t.alert_count === 1 ? '' : 's'}</span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}

                  {/* KYC / KYB documents */}
                  <section className="form-section" style={{ marginTop: '1rem' }}>
                    <header className="form-section__head">
                      <h4 className="form-section__title">KYC documents ({documents.length})</h4>
                      <p className="form-section__subtitle">ID copies, proof of address, registration certificates, source-of-funds evidence.</p>
                    </header>
                    {documents.length === 0 ? (
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary,#607d8b)' }}>
                        No documents uploaded for this customer yet.
                      </p>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-color,#eef0f3)' }}>
                            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Type</th>
                            <th style={{ padding: '0.5rem', textAlign: 'left' }}>File</th>
                            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
                            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Uploaded</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documents.map(d => (
                            <tr key={d.id} style={{ borderBottom: '1px solid var(--border-color,#eef0f3)' }}>
                              <td style={{ padding: '0.5rem' }}>
                                <strong>{d.document_type.replace(/_/g, ' ')}</strong>
                                {d.document_label && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{d.document_label}</div>}
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <div>{d.file_name}</div>
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                                  {d.mime_type} · {Math.round((d.file_size || 0) / 1024)} KB
                                </div>
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <span className={`cust-pill cust-pill--status-${
                                  d.verification_status === 'verified' ? 'active'
                                  : d.verification_status === 'rejected' ? 'rejected'
                                  : 'pending'
                                }`}>{d.verification_status}</span>
                              </td>
                              <td style={{ padding: '0.5rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                {formatLastUpdated(d.uploaded_at)}
                                {d.uploaded_by_email && <div>{d.uploaded_by_email}</div>}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <button type="button" className="btn-cancel"
                                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginRight: '0.25rem' }}
                                  onClick={() => viewDocument(d)}>View</button>
                                <button type="button" className="btn-cancel"
                                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginRight: '0.25rem' }}
                                  onClick={() => downloadDocument(d)}>Download</button>
                                {canVerifyDoc && d.verification_status === 'pending' && (
                                  <>
                                    <button type="button" className="export-button"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginRight: '0.25rem' }}
                                      onClick={() => verifyDocument(d.id, 'verified')}>Verify</button>
                                    <button type="button" className="btn-cancel"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginRight: '0.25rem' }}
                                      onClick={() => verifyDocument(d.id, 'rejected')}>Reject</button>
                                  </>
                                )}
                                {canDeleteDoc && (
                                  <button type="button" className="btn-cancel"
                                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', color: '#b71c1c' }}
                                    onClick={() => deleteDocument(d)}>Delete</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {canUploadDoc && (
                      <button className="export-button" style={{ marginTop: '0.75rem' }}
                              onClick={() => { setDocFileError(''); setDocUploadOpen(true); }}>
                        Upload document
                      </button>
                    )}
                  </section>

                  {/* High-level audit trail — everything that happened to this customer */}
                  <CustomerAuditTrail events={detail.events} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Document upload modal */}
      {docUploadOpen && (
        <div className="modal-overlay" onClick={() => !docUploadBusy && setDocUploadOpen(false)}>
          <div className="modal-content" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => !docUploadBusy && setDocUploadOpen(false)}>×</button>
            <div className="modal-body" style={{ paddingTop: '1.5rem' }}>
              <h3 style={{ margin: '0 0 0.75rem' }}>Upload KYC document</h3>
              <form onSubmit={uploadDocument}>
                <div className="form-group">
                  <label>Document type</label>
                  <select
                    value={docUploadForm.document_type}
                    onChange={e => setDocUploadForm(f => ({ ...f, document_type: e.target.value }))}
                    disabled={docUploadBusy}
                  >
                    <option value="id_copy">ID copy</option>
                    <option value="passport">Passport</option>
                    <option value="proof_of_address">Proof of address</option>
                    <option value="business_reg">Business registration</option>
                    <option value="tax_clearance">Tax clearance</option>
                    <option value="ubo_declaration">UBO declaration</option>
                    <option value="source_of_funds">Source of funds</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Label (optional)</label>
                  <input type="text" maxLength="255"
                    value={docUploadForm.document_label}
                    onChange={e => setDocUploadForm(f => ({ ...f, document_label: e.target.value }))}
                    disabled={docUploadBusy}
                    placeholder="e.g. Passport front page" />
                </div>
                <div className="form-group">
                  <label>Expires (optional)</label>
                  <input type="date"
                    value={docUploadForm.expires_at}
                    onChange={e => setDocUploadForm(f => ({ ...f, expires_at: e.target.value }))}
                    disabled={docUploadBusy} />
                </div>
                <div className="form-group">
                  <label>File (PDF only, max 10 MB)</label>
                  <input type="file" required
                    accept=".pdf,application/pdf"
                    onChange={e => pickDocFile(e.target.files?.[0] || null)}
                    disabled={docUploadBusy} />
                  {docFileError && <span className="form-error">{docFileError}</span>}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                  <button type="button" className="btn-cancel"
                    onClick={() => setDocUploadOpen(false)}
                    disabled={docUploadBusy}>Cancel</button>
                  <button type="submit" className="export-button" disabled={docUploadBusy || !docUploadForm.file}>
                    {docUploadBusy ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* In-page confirm/reason modal — replaces window.confirm + window.prompt */}
      {actionModal && (
        <div className="modal-overlay" onClick={() => !actionBusy && setActionModal(null)}>
          <div
            className="modal-content"
            style={{ maxWidth: '480px' }}
            onClick={e => e.stopPropagation()}
          >
            <button className="modal-close" onClick={() => !actionBusy && setActionModal(null)}>×</button>
            <div className="modal-body" style={{ paddingTop: '1.5rem' }}>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 700 }}>
                {actionModal.title}
              </h3>
              <div className="form-group">
                <label className="required">{actionModal.promptLabel || 'Reason'}</label>
                <textarea
                  rows={3}
                  value={actionReason}
                  onChange={(e) => { setActionReason(e.target.value); if (actionError) setActionError(''); }}
                  disabled={actionBusy}
                  placeholder="Why is this status being changed?"
                  autoFocus
                />
                {actionError && <span className="form-error">{actionError}</span>}
              </div>
              <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setActionModal(null)}
                  disabled={actionBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`export-button${actionBusy ? ' is-loading' : ''}`}
                  onClick={runAction}
                  disabled={actionBusy || !actionReason.trim()}
                >
                  {actionBusy ? 'Working…' : (actionModal.confirmLabel || 'Confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editCustomer && (
        <div className="modal-overlay" onClick={() => setEditCustomer(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Customer — {displayName({ ...editCustomer, ...(editCustomer.individual || {}), ...(editCustomer.business || {}) })}</h2>
              <button className="modal-close" onClick={() => setEditCustomer(null)}>×</button>
            </div>
            <div className="modal-body">
              <CustomerForm
                key={editCustomer.id}
                customer={editCustomer}
                onUpdated={(updated) => {
                  setEditCustomer(null);
                  if (selectedId === updated.id) setDetail(updated);
                  fetchList();
                  fetchStats();
                }}
                onCancel={() => setEditCustomer(null)}
              />
            </div>
          </div>
        </div>
      )}

      {showAddCustomer && (
        <div className="modal-overlay" onClick={() => setShowAddCustomer(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Customer</h2>
              <button className="modal-close" onClick={() => setShowAddCustomer(false)}>×</button>
            </div>
            <div className="modal-body">
              <CustomerForm
                onCreated={() => {
                  setShowAddCustomer(false);
                  fetchList();
                  fetchStats();
                }}
                onCancel={() => setShowAddCustomer(false)}
              />
            </div>
          </div>
        </div>
      )}

      {showBulkUpload && (
        <div className="modal-overlay" onClick={() => setShowBulkUpload(false)}>
          <BulkCustomerUpload
            closeModal={() => setShowBulkUpload(false)}
            onUploaded={() => { fetchList(); fetchStats(); }}
          />
        </div>
      )}
    </div>
  );
}

export default ManageCustomers;
